import {
  TelegramTransactionPartByAmount,
  TelegramTransactionsPaymentsSystemsResponseDto,
  TelegramTransactionsTopUpVariantDto,
} from '@/generated';
import { createMachine, fromPromise, assign } from 'xstate';
import { isTelegramStarsPaymentError } from '@/utils/telegram-stars-payment';

export type InnerAmount = number;

export interface MethodDetails {
  id: string;
  fields: Array<{ key: string; label: string; required?: boolean }>;
}

export interface PurchaseContext {
  availableAmounts: TelegramTransactionsTopUpVariantDto[];
  productId?: string;
  featureIds?: string[];
  amount?: InnerAmount;
  methods: TelegramTransactionsPaymentsSystemsResponseDto[];
  selectedPaymentSystem?: string;
  methodDetails?: MethodDetails;
  parts?: TelegramTransactionPartByAmount[];
  cardComDetailsData?: { parts: number; payerName: string; token?: string };
  partsLoading: boolean;
}

export type PurchaseEvents =
  | { type: 'BACK' }
  | { type: 'SELECT_AMOUNT'; amount: InnerAmount }
  | { type: 'SELECT_METHOD'; paymentSystem: string }
  | { type: 'SUBMIT_METHOD_DATA'; data: Record<string, unknown> }
  | { type: 'GO_PAY_CARD_COM'; parts: number; payerName: string; token?: string }
  | { type: 'RESET' };

export interface PurchaseServices {
  handlePayment: (val: {
    methodId: string;
    amount: number;
    productId?: string;
    featureIds?: string[];
    payerName: string;
    parts: any;
  }) => Promise<void>;
  getPartsOptions(amount: number): Promise<TelegramTransactionPartByAmount[]>;
}

/** Output returned on done when used as a child machine */
export interface PurchaseResult {
  amount: InnerAmount;
  methodId: string;
  orderId?: string;
}

export type PurchaseInput = Partial<Pick<PurchaseContext, 'availableAmounts' | 'methods' | 'productId' | 'featureIds'>>;

/** --- Factory --- */
export function createPurchaseSubflow(services: PurchaseServices) {
  return createMachine({
    types: {} as {
      context: PurchaseContext;
      events: PurchaseEvents;
      input: PurchaseInput;
      output: PurchaseResult;
    },
    id: 'purchaseSubflow',
    context: ({ input }) => ({
      availableAmounts: input.availableAmounts ?? [],
      productId: input.productId || undefined,
      featureIds: input.featureIds || undefined,
      methods: input.methods ?? [],
      methodDetails: undefined,
      parts: undefined,
      cardComDetailsData: undefined,
      partsLoading: false,
    }),
    initial: 'chooseAmount',

    states: {
      /** 1) Choose amount (amounts come from input.availableAmounts) */
      chooseAmount: {
        on: {
          SELECT_AMOUNT: {
            target: 'chooseMethod',
            actions: ({ context, event }) => {
              context.amount = event.amount;
              // reset downstream
              context.selectedPaymentSystem = undefined;
              context.methodDetails = undefined;
              context.parts = undefined;
              context.partsLoading = false;
            },
          },
        },
      },

      /** 2) Choose method (methods come from input.methods) */
      chooseMethod: {
        entry: ({ context }) => {
          // Optional: preselect single method automatically
          if (!context.selectedPaymentSystem && context.methods.length === 1) {
            context.selectedPaymentSystem = context.methods[0].paymentSystem;
          }
          context.partsLoading = true;
          context.parts = undefined;
        },
        invoke: {
          id: 'loadPartsForAmount',
          input: ({ context }) => ({
            amount: context.availableAmounts.find((val) => val.amount === context.amount)?.amount,
          }),
          src: fromPromise(async ({ input }) => {
            if (input.amount == null) throw new Error('No amount');
            return services.getPartsOptions(input.amount);
          }),
          onDone: {
            actions: ({ context, event }) => {
              context.parts = event.output;
              context.partsLoading = false;
            },
          },
          onError: {
            actions: ({ context }) => {
              context.partsLoading = false;
            },
          },
        },
        on: {
          BACK: 'chooseAmount',
          SELECT_METHOD: [
            {
              guard: ({ event }) => event.paymentSystem === 'MANUAL',
              target: 'manualTransfer',
              actions: ({ context, event }) => {
                context.selectedPaymentSystem = event.paymentSystem;
                context.methodDetails = undefined;
              },
            },
            {
              guard: ({ event }) => event.paymentSystem === 'CARD_COM',
              target: 'cardComDetails',
              actions: ({ context, event }) => {
                context.selectedPaymentSystem = event.paymentSystem;
                context.methodDetails = undefined;
              },
            },
            {
              guard: ({ context, event }) => context.methods.some((m) => m.paymentSystem === event.paymentSystem),
              target: 'handleMethod',
              actions: ({ context, event }) => {
                context.selectedPaymentSystem = event.paymentSystem;
                context.methodDetails = undefined;
              },
            },
          ],
        },
      },
      cardComDetails: {
        on: {
          BACK: 'chooseMethod',
          GO_PAY_CARD_COM: [
            {
              target: 'handleMethod',
              actions: assign(({ context, event }) => {
                context.cardComDetailsData = event;
                context.selectedPaymentSystem = 'CARD_COM';
                return context;
              }),
            },
          ],
        },
      },
      /** 3) Load details for selected method */
      handleMethod: {
        invoke: {
          id: 'handleMethodItself',
          input: ({ context }) => {
            if (context.amount == null) throw new Error('No amount');

            if (context.selectedPaymentSystem === 'CARD_COM') {
              const cardComDetailsData = context.cardComDetailsData;
              if (context.productId && context.featureIds) {
                return {
                  amount: context.amount,
                  methodId: context.selectedPaymentSystem,
                  featureIds: context.featureIds,
                  productId: context.productId,
                  payerName: cardComDetailsData?.payerName,
                  parts: cardComDetailsData?.parts,
                  token: cardComDetailsData?.token || undefined,
                };
              }
              return {
                amount: context.amount,
                methodId: context.selectedPaymentSystem,
                payerName: cardComDetailsData?.payerName,
                parts: cardComDetailsData?.parts,
                token: cardComDetailsData?.token || undefined,
              };
            }

            if (context.productId && context.featureIds) {
              return {
                amount: context.amount,
                methodId: context.selectedPaymentSystem,
                featureIds: context.featureIds,
                productId: context.productId,
              };
            }
            return {
              amount: context.amount,
              methodId: context.selectedPaymentSystem,
            };
          },

          src: fromPromise(async ({ input }) => {
            await services.handlePayment(input);
          }),
          onDone: {
            target: 'thankYou',
            actions: ({ context, event }) => {
              context.methodDetails = event.output;
            },
          },
          onError: [
            {
              guard: ({ event }) => isTelegramStarsPaymentError(event.error, 'cancelled'),
              target: 'chooseMethod',
            },
            {
              target: 'error',
            },
          ],
        },
      },

      manualTransfer: {
        on: {
          BACK: 'chooseMethod',
        },
      },

      error: {
        on: {
          BACK: 'chooseMethod',
        },
      },

      thankYou: {
        on: {
          RESET: {
            target: 'chooseAmount',
            actions: ({ context }) => {
              context.amount = undefined;
              context.selectedPaymentSystem = undefined;
              context.methodDetails = undefined;
              (context as any)._tmpSubmission = undefined;
            },
          },
        },
        type: 'final',
        output: ({ context }) => ({
          amount: context.amount!,
          methodId: context.selectedPaymentSystem!,
        }),
      },
    },
  });
}

// Helper type for consumers
